import { useState, useEffect } from "react";
import {
  Shield,
  Plus,
  Trash2,
  Play,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Building2,
  Table2,
  Users,
  RefreshCw,
  Eye,
  Info,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import api from "@/services/api";

const StatusBadge = ({ status }) => {
  const config = {
    active: { icon: CheckCircle2, label: "Active", className: "bg-green-100 text-green-700 border-green-200" },
    pending: { icon: AlertCircle, label: "Pending", className: "bg-yellow-100 text-yellow-700 border-yellow-200" },
    error: { icon: AlertCircle, label: "Error", className: "bg-red-100 text-red-700 border-red-200" },
  };

  const { icon: Icon, label, className } = config[status] || config.pending;

  return (
    <Badge variant="outline" className={className}>
      <Icon className="w-3 h-3 mr-1" />
      {label}
    </Badge>
  );
};

const PolicyCard = ({ policy, onApply, onDelete, applying }) => {
  return (
    <Card className="relative overflow-hidden" data-testid={`rls-policy-${policy.id}`}>
      <div className={`absolute top-0 left-0 w-full h-1 ${
        policy.status === 'active' ? 'bg-green-500' : 
        policy.status === 'error' ? 'bg-red-500' : 'bg-yellow-500'
      }`} />
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-purple-100 rounded-xl">
              <Shield className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <CardTitle className="text-lg">{policy.name}</CardTitle>
              <CardDescription className="font-mono text-xs mt-0.5">
                {policy.dataset}.{policy.table_name}
              </CardDescription>
            </div>
          </div>
          <StatusBadge status={policy.status} />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Tenant Info */}
        <div className="flex items-center gap-2 text-sm">
          <Building2 className="w-4 h-4 text-blue-500" />
          <span className="text-muted-foreground">Tenant:</span>
          <Badge variant="secondary">{policy.tenant_id}</Badge>
        </div>

        {/* Filter Expression */}
        <div className="p-3 bg-muted rounded-lg">
          <p className="text-xs text-muted-foreground mb-1">Filter Expression:</p>
          <code className="text-sm font-mono text-purple-700">{policy.filter_expression}</code>
        </div>

        {/* Grantees */}
        {policy.grantees && policy.grantees.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Users className="w-4 h-4 text-green-500" />
              <span className="text-muted-foreground">Grantees:</span>
            </div>
            <div className="flex flex-wrap gap-1">
              {policy.grantees.map((grantee, idx) => (
                <Badge key={idx} variant="outline" className="text-xs">
                  {grantee}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Error message */}
        {policy.error_message && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-xs text-red-600">{policy.error_message}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          {policy.status !== 'active' && (
            <Button 
              variant="default" 
              size="sm" 
              onClick={() => onApply(policy.id)}
              disabled={applying === policy.id}
            >
              {applying === policy.id ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Play className="w-4 h-4 mr-1" />
              )}
              Apply Policy
            </Button>
          )}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive ml-auto">
                <Trash2 className="w-4 h-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete RLS Policy</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete &quot;{policy.name}&quot;? 
                  {policy.status === 'active' && " This will also remove the policy from BigQuery."}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => onDelete(policy.id)}
                  className="bg-destructive text-destructive-foreground"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardContent>
    </Card>
  );
};

export default function RLS() {
  const [policies, setPolicies] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [bigqueryConnections, setBigqueryConnections] = useState([]);
  const [goldTables, setGoldTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(null);

  // Create policy dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState("");
  const [tablesLoading, setTablesLoading] = useState(false);
  const [policyForm, setPolicyForm] = useState({
    name: "",
    table_name: "",
    tenant_id: "",
    grantees: "",
    description: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedConnection) {
      fetchGoldTables(selectedConnection);
    }
  }, [selectedConnection]);

  const fetchData = async () => {
    try {
      const [policiesData, tenantsData, bqData] = await Promise.all([
        api.getRLSPolicies(),
        api.getTenants(),
        api.getBigQueryConnections(),
      ]);
      setPolicies(policiesData);
      setTenants(tenantsData);
      setBigqueryConnections(bqData.filter(c => c.status === "connected"));
      
      // Set default connection if available
      if (bqData.length > 0) {
        const connected = bqData.find(c => c.status === "connected");
        if (connected) {
          setSelectedConnection(connected.id);
        }
      }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const fetchGoldTables = async (connectionId) => {
    setTablesLoading(true);
    try {
      const data = await api.getGoldTablesForRLS(connectionId);
      setGoldTables(data.tables || []);
    } catch (error) {
      console.error("Failed to load gold tables:", error);
      setGoldTables([]);
    } finally {
      setTablesLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setPolicyForm({
      name: "",
      table_name: "",
      tenant_id: "",
      grantees: "",
      description: "",
    });
    setDialogOpen(true);
  };

  const handleCreatePolicy = async (e) => {
    e.preventDefault();
    
    if (!selectedConnection) {
      toast.error("Please select a BigQuery connection");
      return;
    }

    try {
      const data = {
        name: policyForm.name,
        table_name: policyForm.table_name,
        tenant_id: policyForm.tenant_id,
        grantees: policyForm.grantees.split(",").map(g => g.trim()).filter(g => g),
        description: policyForm.description || null,
      };

      await api.createRLSPolicy(data, selectedConnection);
      toast.success("RLS policy created");
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create policy");
    }
  };

  const handleApplyPolicy = async (policyId) => {
    setApplying(policyId);
    try {
      await api.applyRLSPolicy(policyId);
      toast.success("RLS policy applied to BigQuery");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to apply policy");
    } finally {
      setApplying(null);
    }
  };

  const handleDeletePolicy = async (policyId) => {
    try {
      await api.deleteRLSPolicy(policyId);
      toast.success("RLS policy deleted");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete policy");
    }
  };

  // Auto-generate policy name when tenant and table are selected
  const generatePolicyName = (tenantId, tableName) => {
    if (tenantId && tableName) {
      return `rls_${tenantId.toLowerCase()}_${tableName.toLowerCase()}`;
    }
    return "";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="rls-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Shield className="w-5 h-5 text-purple-600" />
            Row-Level Security
          </h2>
          <p className="text-sm text-muted-foreground">
            Manage RLS policies for tenant data isolation in Gold layer tables
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} size="sm">
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
          <Button onClick={handleOpenDialog} data-testid="create-rls-policy-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create Policy
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-gradient-to-r from-purple-50 via-blue-50 to-pink-50 border-purple-200">
        <CardContent className="py-4">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-white rounded-lg shadow-sm">
              <Info className="w-6 h-6 text-purple-500" />
            </div>
            <div>
              <h3 className="font-semibold">How RLS Works</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Row-Level Security policies filter data at query time based on <code className="bg-white px-1 rounded">tenant_id</code>. 
                Each tenant only sees rows where <code className="bg-white px-1 rounded">tenant_id</code> matches their policy.
                Policies are applied to Gold layer tables in BigQuery.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* BigQuery Connection Selector */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center gap-4">
            <Label className="whitespace-nowrap">BigQuery Connection:</Label>
            <Select value={selectedConnection} onValueChange={setSelectedConnection}>
              <SelectTrigger className="w-64">
                <SelectValue placeholder="Select connection" />
              </SelectTrigger>
              <SelectContent>
                {bigqueryConnections.map((conn) => (
                  <SelectItem key={conn.id} value={conn.id}>
                    {conn.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedConnection && (
              <span className="text-sm text-muted-foreground">
                {goldTables.length} Gold tables found
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Gold Tables with tenant_id */}
      {selectedConnection && goldTables.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Table2 className="w-4 h-4" />
              Gold Layer Tables
            </CardTitle>
            <CardDescription>
              Tables with <code className="bg-muted px-1 rounded">tenant_id</code> column can have RLS policies
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Table Name</TableHead>
                  <TableHead>Rows</TableHead>
                  <TableHead>Has tenant_id</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {goldTables.map((table) => (
                  <TableRow key={table.table_name}>
                    <TableCell className="font-mono text-sm">{table.table_name}</TableCell>
                    <TableCell>{table.num_rows?.toLocaleString() || 0}</TableCell>
                    <TableCell>
                      {table.has_tenant_id ? (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Yes
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-gray-50 text-gray-500">
                          No
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {table.has_tenant_id && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            setPolicyForm({
                              ...policyForm,
                              table_name: table.table_name,
                              name: generatePolicyName(policyForm.tenant_id, table.table_name),
                            });
                            setDialogOpen(true);
                          }}
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Add Policy
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Policies Grid */}
      <div>
        <h3 className="text-base font-semibold mb-4">Configured Policies ({policies.length})</h3>
        {policies.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Shield className="w-12 h-12 text-muted-foreground mb-4" />
              <h3 className="font-semibold text-lg">No RLS Policies</h3>
              <p className="text-muted-foreground text-sm mt-1 text-center max-w-md">
                Create RLS policies to restrict tenant access to their own data in Gold layer tables.
              </p>
              <Button className="mt-4" onClick={handleOpenDialog}>
                <Plus className="w-4 h-4 mr-2" />
                Create First Policy
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {policies.map((policy) => (
              <PolicyCard
                key={policy.id}
                policy={policy}
                onApply={handleApplyPolicy}
                onDelete={handleDeletePolicy}
                applying={applying}
              />
            ))}
          </div>
        )}
      </div>

      {/* Create Policy Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-purple-600" />
              Create RLS Policy
            </DialogTitle>
            <DialogDescription>
              Create a Row-Level Security policy to restrict tenant data access in Gold layer tables
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreatePolicy} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tenant *</Label>
                <Select
                  value={policyForm.tenant_id}
                  onValueChange={(val) => setPolicyForm({ 
                    ...policyForm, 
                    tenant_id: val,
                    name: generatePolicyName(val, policyForm.table_name)
                  })}
                >
                  <SelectTrigger data-testid="rls-tenant-select">
                    <SelectValue placeholder="Select tenant" />
                  </SelectTrigger>
                  <SelectContent>
                    {tenants.map((t) => (
                      <SelectItem key={t.tenant_id} value={t.tenant_id}>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-3 h-3" />
                          {t.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Gold Table *</Label>
                <Select
                  value={policyForm.table_name}
                  onValueChange={(val) => setPolicyForm({ 
                    ...policyForm, 
                    table_name: val,
                    name: generatePolicyName(policyForm.tenant_id, val)
                  })}
                  disabled={tablesLoading}
                >
                  <SelectTrigger data-testid="rls-table-select">
                    <SelectValue placeholder={tablesLoading ? "Loading..." : "Select table"} />
                  </SelectTrigger>
                  <SelectContent>
                    {goldTables.filter(t => t.has_tenant_id).map((t) => (
                      <SelectItem key={t.table_name} value={t.table_name}>
                        {t.table_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Policy Name *</Label>
              <Input
                value={policyForm.name}
                onChange={(e) => setPolicyForm({ ...policyForm, name: e.target.value })}
                placeholder="e.g., rls_tenant_orders"
                required
                data-testid="rls-policy-name"
              />
              <p className="text-xs text-muted-foreground">
                Unique identifier for this policy in BigQuery
              </p>
            </div>

            <div className="space-y-2">
              <Label>Grantees (Optional)</Label>
              <Textarea
                value={policyForm.grantees}
                onChange={(e) => setPolicyForm({ ...policyForm, grantees: e.target.value })}
                placeholder="user:alice@example.com, group:analysts@example.com"
                rows={2}
              />
              <p className="text-xs text-muted-foreground">
                Comma-separated list of users/groups. Leave empty for allAuthenticatedUsers.
              </p>
            </div>

            {policyForm.tenant_id && policyForm.table_name && (
              <div className="p-3 bg-purple-50 rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Filter Expression:</p>
                <code className="text-sm font-mono text-purple-700">
                  tenant_id = &apos;{policyForm.tenant_id}&apos;
                </code>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={!policyForm.name || !policyForm.tenant_id || !policyForm.table_name}
                data-testid="create-rls-submit"
              >
                Create Policy
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
